/**
 * Toast Notification System
 * Displays attractive toast messages instead of browser alerts
 */

class ToastNotification {
  constructor(options = {}) {
    this.options = {
      position: options.position || 'top-center',
      duration: options.duration || 5000,
      maxToasts: options.maxToasts || 5,
      containerClass: options.containerClass || 'toast-container',
      zIndex: options.zIndex || 9999
    };
    
    this.toastContainer = null;
    this.toasts = [];
    this.initialize();
  }
  
  initialize() {
    // Create toast container if it doesn't exist
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.className = `${this.options.containerClass} ${this.options.position}`;
      document.body.appendChild(this.toastContainer);
    }
  }
  
  /**
   * Show a toast notification
   * @param {string} message - The message to display
   * @param {string} type - The type of toast (success, error, warning, info)
   * @param {number} duration - How long to display the toast (in ms)
   */
  show(message, type = 'success', duration = this.options.duration) {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast toast-${type} toast-animation`;
    
    // Create icon based on type
    const icon = document.createElement('div');
    icon.className = 'toast-icon';
    
    if (type === 'success') {
      // Green checkmark for success
      icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      `;
    } else if (type === 'error') {
      // X icon for error
      icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 6L6 18M6 6l12 12"></path>
        </svg>
      `;
    } else if (type === 'warning') {
      // Exclamation mark for warning
      icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
      `;
    } else {
      // Info icon
      icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `;
    }
    
    // Create content container
    const content = document.createElement('div');
    content.className = 'toast-content';
    content.innerHTML = message; // Use innerHTML instead of textContent to support HTML
    
    // Assemble toast
    toast.appendChild(icon);
    toast.appendChild(content);
    
    // Manage toast queue
    if (this.toasts.length >= this.options.maxToasts) {
      this.removeToast(this.toasts[0]);
    }
    
    // Add to container and array
    this.toastContainer.appendChild(toast);
    this.toasts.push(toast);
    
    // Set auto-remove timeout
    toast._timeout = setTimeout(() => {
      this.removeToast(toast);
    }, duration);
    
    return toast;
  }
  
  /**
   * Show a success toast
   * @param {string} message - The message to display
   * @param {number} duration - How long to display the toast (in ms)
   */
  success(message, duration = this.options.duration) {
    return this.show(message, 'success', duration);
  }
  
  /**
   * Show an error toast
   * @param {string} message - The message to display
   * @param {number} duration - How long to display the toast (in ms)
   */
  error(message, duration = this.options.duration) {
    return this.show(message, 'error', duration);
  }
  
  /**
   * Show a warning toast
   * @param {string} message - The message to display
   * @param {number} duration - How long to display the toast (in ms)
   */
  warning(message, duration = this.options.duration) {
    return this.show(message, 'warning', duration);
  }
  
  /**
   * Show an info toast
   * @param {string} message - The message to display
   * @param {number} duration - How long to display the toast (in ms)
   */
  info(message, duration = this.options.duration) {
    return this.show(message, 'info', duration);
  }
  
  /**
   * Remove a specific toast
   * @param {HTMLElement} toast - The toast element to remove
   */
  removeToast(toast) {
    if (!toast || !toast.parentNode) return;
    
    // Clear timeout to prevent duplicate removal attempts
    if (toast._timeout) {
      clearTimeout(toast._timeout);
      toast._timeout = null;
    }
    
    // Add fade out animation
    toast.classList.add('toast-fadeout');
    
    // Remove after animation
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
        this.toasts = this.toasts.filter(t => t !== toast);
      }
    }, 300); // Animation duration
  }
  
  /**
   * Remove all toasts
   */
  clearAll() {
    this.toasts.forEach(toast => {
      if (toast._timeout) {
        clearTimeout(toast._timeout);
      }
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    });
    this.toasts = [];
  }
}

// Create global instance
window.toast = new ToastNotification();

// Replace window.alert with toast.success
const originalAlert = window.alert;
window.alert = function(message) {
  if (window.toast) {
    return window.toast.success(message);
  }
  return originalAlert(message);
};
